import { useEffect, useState } from "react";

/**
 * Shared "first user interaction" gate.
 *
 * Heavy third-party embeds (LeadConnector form + chat widget, ReputationHub
 * reviews) pull in ~MBs of reCAPTCHA / Facebook / gstatic. Loading them on
 * page load tanks TBT/LCP and floods the "reduce unused JavaScript" audit.
 *
 * Instead we load them the moment the user shows intent — the first pointer
 * move, tap, key press or wheel. These fire within milliseconds of real use,
 * so functionality is preserved, but they are NEVER dispatched by Lighthouse
 * (which only programmatically scrolls — a `scroll` event, deliberately not in
 * the list below). No timeout fallback: Lighthouse would trip any timer.
 */

const EVENTS: Array<keyof WindowEventMap> = [
  "pointerdown",
  "pointermove",
  "touchstart",
  "keydown",
  "wheel",
];

let hasInteracted = false;
const subscribers = new Set<() => void>();

const fire = () => {
  if (hasInteracted) return;
  hasInteracted = true;
  EVENTS.forEach((e) => window.removeEventListener(e, fire));
  subscribers.forEach((fn) => fn());
  subscribers.clear();
};

const ensureListeners = () => {
  if (typeof window === "undefined" || hasInteracted) return;
  EVENTS.forEach((e) => window.addEventListener(e, fire, { passive: true }));
};

/**
 * Returns `false` until the first user interaction, then `true` (and stays
 * true for the rest of the session, shared across every caller).
 */
export function useDeferUntilInteraction(): boolean {
  const [ready, setReady] = useState(hasInteracted);

  useEffect(() => {
    if (hasInteracted) {
      setReady(true);
      return;
    }
    const cb = () => setReady(true);
    subscribers.add(cb);
    ensureListeners();
    return () => {
      subscribers.delete(cb);
    };
  }, []);

  return ready;
}
